"use client";

import Skeleton from "@/components/atoms/Skeleton";
import useHasHydrated from "@/lib/hooks/useHasHydrated";
import { clsx } from "@frend-digital/ui";
import { useRef, useState } from "react";
import ReactPlayer from "react-player";
import styles from "./ReactVideoPlayer.module.css";

type VideoProps = {
  media: { url: string };
  onPlay: () => void;
  onPause: () => void;
  videoRef: React.RefObject<ReactPlayer>;
  shouldAutoPlay?: boolean;
  shouldLoop?: boolean;
  className?: string;
};

const VideoElement = ({
  media,
  onPlay,
  onPause,
  videoRef,
  shouldAutoPlay,
  shouldLoop,
  playing,
}: VideoProps & { playing: boolean }) => {
  const hydrated = useHasHydrated();
  if (!hydrated) return <Skeleton height="100%" />;
  return (
    <ReactPlayer
      onPlay={onPlay}
      onPause={onPause}
      url={media.url}
      ref={videoRef}
      playing={playing}
      playsInline
      loop={shouldLoop}
      autoPlay={shouldAutoPlay}
      width={"100%"}
      height={"100%"}
      className={styles.reactVideoPlayer}
      fallback={<Skeleton height="100%" />}
      controls={true}
    />
  );
};

type ReactPlayerProps = {
  media: { url: string };
  shouldAutoPlay?: boolean;
  shouldLoop?: boolean;
  className?: string;
};

export const ReactVideoPlayer = ({
  media,
  shouldAutoPlay = true,
  shouldLoop = true,
  className,
}: ReactPlayerProps) => {
  const reactVideoRef = useRef<ReactPlayer>(null!);

  const [isPlaying, setIsPlaying] = useState(shouldAutoPlay);

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  return (
    <div className={clsx(styles.videoWrapper, className)}>
      <VideoElement
        media={media}
        onPause={handlePause}
        onPlay={handlePlay}
        videoRef={reactVideoRef}
        shouldLoop={shouldLoop}
        playing={isPlaying}
      />
    </div>
  );
};
